import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import axios from 'axios';
import { format } from 'date-fns';
import { db } from '../firebase-config';
import { getUrlParams } from '../common';
import { pilotAttach, pilotRequest, pilotEnrolmentId } from '../bookingPilotClient';
import MyCalendar from '../components/Calendar';
import AddressSearch from '../components/AddressSearch';
import Input from '../components/Input';
import Dropdown from '../components/Dropdown';
import QldStageRenderer from '../components/QldStageRenderer';
import Logo from '../assets/Logo.svg';
import { isValidQldMobile, normalizeQldMobile } from '../qldContact';
import { clearPageMessage, showLoadingMessage, showSuccessMessage, showWarningMessage } from '../successMessage';
import {
    buildQldStudentPayload,
    buildStageCompletionPatch,
    formatQldNameInput,
    getCurrentStudentStage,
    getNextLockedStage,
    isValidEmail,
    isQldLinkActive,
    QLD_STAGE_KEYS,
    QLD_STAGE_LABELS,
} from '../qldWorkflow';

const findQldStudentByIdentityUrl = 'https://australia-southeast1-upskillinstitute-au.cloudfunctions.net/findQldStudentByIdentity';
const areEqual = (left, right) => JSON.stringify(left || null) === JSON.stringify(right || null);
const TEMP_FORCE_STUDENT_STAGE_KEY = '';
const australianStateOptions = ['NSW', 'QLD', 'ACT', 'SA', 'NT', 'VIC', 'TAS', 'WA'];

const QldEoi = () => {
    const params = getUrlParams();
    const linkId = params.linkId || '';
    const sidParam = params.sid || '';
    const [studentId, setStudentId] = useState(sidParam || (pilotEnrolmentId() ? '' : window.localStorage.getItem(`qld-link-${linkId}`)) || '');
    const [pilotLoading, setPilotLoading] = useState(Boolean(pilotEnrolmentId()));
    const [pilotError, setPilotError] = useState('');
    const [linkDoc, setLinkDoc] = useState(null);
    const [studentDoc, setStudentDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const studentDocRef = useRef(null);
    const stageProgressTimerRef = useRef(null);
    const queuedStageProgressRef = useRef(null);
    const stageProgressWriteInFlightRef = useRef(false);

    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [dob, setDob] = useState(null);
    const [mobile, setMobile] = useState('');
    const [registrationErrors, setRegistrationErrors] = useState([]);
    const [address, setAddress] = useState('');
    const [addressManualFill, setAddressManualFill] = useState(false);
    const [lockAddressManualFields, setLockAddressManualFields] = useState(false);
    const [addressLookupLoading, setAddressLookupLoading] = useState(false);
    const [streetNumber, setStreetNumber] = useState('');
    const [streetName, setStreetName] = useState('');
    const [suburb, setSuburb] = useState('');
    const [state, setState] = useState('');
    const [postcode, setPostcode] = useState('');
    const [postCompletionStage, setPostCompletionStage] = useState('');
    const isAddressPending = Boolean(address.trim()) && !addressManualFill;
    const isRegistrationBlocked = submitting || addressLookupLoading || isAddressPending;
    useEffect(() => {
        if (!pilotEnrolmentId()) return;
        pilotRequest('formContext').then(context => {
            if (context.course !== 'white-card-qld' || context.qldLinkId !== linkId) throw new Error('This QLD link does not match your booking.');
            setFirstName(context.person.firstName); setLastName(context.person.lastName);
            setEmail(context.person.email); setMobile(context.person.phoneNumber); setDob(new Date(`${context.person.dob}T12:00:00`));
            if (sidParam) return pilotRequest('attachQld', { documentId: sidParam });
        }).catch(error => setPilotError(error.message)).finally(() => setPilotLoading(false));
    }, [linkId, sidParam]);

    useEffect(() => {
        studentDocRef.current = studentDoc;
    }, [studentDoc]);

    useEffect(() => {
        const loadLink = async () => {
            if (!linkId) {
                setError('This QLD student link is invalid.');
                setLoading(false);
                return;
            }

            const snapshot = await getDoc(doc(db, 'qldLinks', linkId));
            if (!snapshot.exists()) {
                setError('This QLD student link was not found.');
                setLoading(false);
                return;
            }

            const nextLinkDoc = { id: snapshot.id, ...snapshot.data() };
            if (!isQldLinkActive(nextLinkDoc)) {
                const savedSession = sidParam ? await getDoc(doc(db, 'qldStudents', sidParam)) : null;
                if (nextLinkDoc.active && savedSession?.exists() && savedSession.data().linkId === linkId) {
                    setLinkDoc(nextLinkDoc);
                    setLoading(false);
                    return;
                }
                setError('This QLD student link has expired.');
                setLoading(false);
                return;
            }

            setLinkDoc(nextLinkDoc);
            setLoading(false);
        };

        loadLink().catch(() => {
            setError('Unable to load this student session. Please try again.');
            setLoading(false);
        });
    }, [linkId, sidParam]);

    useEffect(() => {
        if (!studentId) {
            return undefined;
        }

        const unsubscribe = onSnapshot(doc(db, 'qldStudents', studentId), (snapshot) => {
            if (!snapshot.exists()) {
                return;
            }

            const nextStudentDoc = { id: snapshot.id, ...snapshot.data() };
            setStudentDoc(nextStudentDoc);
            window.localStorage.setItem(`qld-link-${linkId}`, snapshot.id);

            if (!sidParam) {
                const nextUrl = new URL(window.location.href);
                nextUrl.searchParams.set('linkId', linkId);
                nextUrl.searchParams.set('sid', snapshot.id);
                window.history.replaceState({}, '', nextUrl.toString());
            }
        });

        return () => unsubscribe();
    }, [studentId, linkId, sidParam]);

    const activeStageKey = useMemo(() => {
        if (TEMP_FORCE_STUDENT_STAGE_KEY && studentDoc) {
            return TEMP_FORCE_STUDENT_STAGE_KEY;
        }

        return getCurrentStudentStage(studentDoc);
    }, [studentDoc]);
    const nextLockedStage = useMemo(() => getNextLockedStage(studentDoc), [studentDoc]);
    const pendingTrainerReviewStage = useMemo(() => (
        studentDoc?.stages
            ? QLD_STAGE_KEYS.find((key) => studentDoc.stages[key]?.reviewStatus === 'submitted_for_review')
            : null
    ), [studentDoc]);
    const allStagesComplete = useMemo(() => (
        studentDoc?.stages
            ? QLD_STAGE_KEYS.every((key) => studentDoc.stages[key]?.completed)
            : false
    ), [studentDoc]);

    useEffect(() => {
        if (!postCompletionStage || !studentDoc?.stages) {
            return;
        }

        if (
            postCompletionStage === 'eoi'
            && studentDoc.stages.assessment1?.unlocked
            && studentDoc.stages.eoi?.reviewStatus !== 'submitted_for_review'
        ) {
            setPostCompletionStage('');
            return;
        }

        if (
            postCompletionStage === 'lln'
            && studentDoc.stages.lln?.reviewStatus === 'not_satisfactory'
        ) {
            setPostCompletionStage('');
        }
    }, [postCompletionStage, studentDoc]);

    const handleRegister = async () => {
        const nextErrors = [];
        if (!firstName) {
            nextErrors.push('firstName');
        }
        if (!lastName) {
            nextErrors.push('lastName');
        }
        if (!email) {
            nextErrors.push('email');
        }
        if (!dob) {
            nextErrors.push('dob');
        }
        if (!mobile.trim()) {
            nextErrors.push('mobile');
        }

        if (!addressManualFill) {
            if (!address.trim()) {
                nextErrors.push('address');
            }
        } else {
            if (!streetNumber.trim()) {
                nextErrors.push('streetNumber');
            }
            if (!streetName.trim()) {
                nextErrors.push('streetName');
            }
            if (!suburb.trim()) {
                nextErrors.push('suburb');
            }
            if (!(state?.value || state?.label || state)) {
                nextErrors.push('state');
            }
            if (!postcode.trim()) {
                nextErrors.push('postcode');
            }
        }

        if (nextErrors.length > 0 || !linkDoc) {
            setRegistrationErrors(nextErrors);
            setError('Please complete all fields.');
            return;
        }

        if (!isValidQldMobile(mobile)) {
            setRegistrationErrors(['mobile']);
            setError('Enter a valid mobile number (8-15 digits, including country code if needed).');
            return;
        }

        if (!isValidEmail(email)) {
            setRegistrationErrors(['email']);
            setError('Enter a valid email address.');
            return;
        }

        if (!postcode.match(/^\d+$/) || postcode.length !== 4) {
            setRegistrationErrors(['postcode']);
            setError('Enter a valid postcode.');
            return;
        }

        setSubmitting(true);
        showLoadingMessage('Registering Student');
        setError('');
        setRegistrationErrors([]);

        try {
        const normalizedEmail = email.trim().toLowerCase();
        const dobKey = format(dob, 'yyyy-MM-dd');
        const existingStudentResponse = await axios.post(findQldStudentByIdentityUrl, {
            linkId: linkDoc.id,
            email: normalizedEmail,
            dobKey,
        });

        if (existingStudentResponse.data?.studentId) {
            if (pilotEnrolmentId()) await pilotAttach('attachQld', { documentId: existingStudentResponse.data.studentId });
            await updateDoc(doc(db, 'qldStudents', existingStudentResponse.data.studentId), {
                mobile: normalizeQldMobile(mobile),
                updatedAt: new Date().toISOString(),
            });
            setStudentId(existingStudentResponse.data.studentId);
            setSubmitting(false);
            showSuccessMessage('Student registration found');
            return;
        }

        const payload = buildQldStudentPayload({
            linkId: linkDoc.id,
            trainerId: linkDoc.trainerId,
            firstName,
            middleName,
            lastName,
            email: normalizedEmail,
            dob: dob.toISOString(),
            mobile: normalizeQldMobile(mobile),
            address,
            streetNumber,
            streetName,
            suburb,
            state: state?.value || state?.label || state,
            postcode,
        });

        const studentRef = await addDoc(collection(db, 'qldStudents'), payload);
        if (pilotEnrolmentId()) await pilotAttach('attachQld', { documentId: studentRef.id });
        setStudentId(studentRef.id);
        setSubmitting(false);
        showSuccessMessage('Student registered successfully');
        } catch (registrationError) {
            clearPageMessage();
            setSubmitting(false);
            setError('Registration could not be completed. Please try again.');
            showWarningMessage('Student registration could not be completed. Please try again.');
            console.error('QLD student registration failed', registrationError);
        }
    };

    const flushQueuedStageProgress = useCallback(async () => {
        if (stageProgressWriteInFlightRef.current || !queuedStageProgressRef.current) {
            return;
        }

        const pendingProgress = queuedStageProgressRef.current;
        queuedStageProgressRef.current = null;
        const currentStudentDoc = studentDocRef.current;
        if (!currentStudentDoc || areEqual(currentStudentDoc.responses?.[pendingProgress.stageKey], pendingProgress.responseData)) {
            return;
        }

        stageProgressWriteInFlightRef.current = true;
        try {
            await updateDoc(doc(db, 'qldStudents', currentStudentDoc.id), {
                responses: {
                    ...(currentStudentDoc.responses || {}),
                    [pendingProgress.stageKey]: pendingProgress.responseData,
                },
                updatedAt: new Date().toISOString(),
            });
        } catch (progressError) {
            console.error('Unable to save QLD stage progress', progressError);
        } finally {
            stageProgressWriteInFlightRef.current = false;
            if (queuedStageProgressRef.current) {
                window.clearTimeout(stageProgressTimerRef.current);
                stageProgressTimerRef.current = window.setTimeout(flushQueuedStageProgress, 1000);
            }
        }
    }, []);

    const queueStageProgress = useCallback((stageKey, responseData) => {
        if (!studentDocRef.current) {
            return;
        }

        queuedStageProgressRef.current = { stageKey, responseData };
        window.clearTimeout(stageProgressTimerRef.current);
        stageProgressTimerRef.current = window.setTimeout(flushQueuedStageProgress, 1000);
    }, [flushQueuedStageProgress]);

    useEffect(() => () => {
        window.clearTimeout(stageProgressTimerRef.current);
    }, []);

    const persistStageProgress = async (stageKey, responseData, markComplete = false) => {
        const currentStudentDoc = studentDocRef.current;
        if (!currentStudentDoc) {
            return;
        }

        if (!markComplete) {
            queueStageProgress(stageKey, responseData);
            return;
        }

        queuedStageProgressRef.current = null;
        window.clearTimeout(stageProgressTimerRef.current);

        const patch = buildStageCompletionPatch(currentStudentDoc, stageKey, responseData);

        await updateDoc(doc(db, 'qldStudents', currentStudentDoc.id), patch);

        if (markComplete && stageKey === 'lln') {
            setPostCompletionStage('lln');
        }

        if (markComplete && stageKey === 'eoi') {
            setPostCompletionStage('eoi');
        }
    };

    // A pilot student arrives here from their portal, so every state must offer a way back.
    const pilotReturn = pilotEnrolmentId()
        ? <p className='qldPilotReturn'><a href='/student-pilot'>Return to my courses</a></p>
        : null;
    if (pilotError) return <div className='form-wrapper'><p role='alert'>{pilotError}</p><a href='/student-pilot'>Return to my courses</a></div>;
    if (loading || pilotLoading) {
        return <div className='form-wrapper'><h1>Loading...</h1></div>;
    }

    if (error && !linkDoc) {
        return <div className='form-wrapper'><h1>QLD Student Flow</h1><p>{error}</p></div>;
    }

    if (!studentId || !studentDoc) {
        return (
            <div className='form-wrapper qld-branded-shell'>
                <div className='qld-page-brand'>
                    <div>
                        <h1>White Card QLD Student Registration</h1>
                        <p>Please provide your details to begin.</p>
                    </div>
                    <img src={Logo} alt='Upskill Institute' />
                </div>
                <Input type='text' errorMessage={registrationErrors.includes('firstName') && registrationErrors.length === 1 ? error : ''} errors={registrationErrors} name='firstName' value={firstName} setValue={(value) => setFirstName(formatQldNameInput(value))} label='First Name' />
                <Input type='text' errors={registrationErrors} name='middleName' value={middleName} setValue={(value) => setMiddleName(formatQldNameInput(value))} label='Middle Name' />
                <Input type='text' errorMessage={registrationErrors.includes('lastName') && registrationErrors.length === 1 ? error : ''} errors={registrationErrors} name='lastName' value={lastName} setValue={(value) => setLastName(formatQldNameInput(value))} label='Last Name' />
                <Input type='email' errorMessage={registrationErrors.includes('email') && registrationErrors.length === 1 ? error : ''} errors={registrationErrors} name='email' value={email} setValue={setEmail} label='Email' />
                <label className='fieldHeading' style={{ fontWeight: 'normal' }}>Date of Birth</label>
                <MyCalendar
                    startDate={dob}
                    setStartDate={setDob}
                    errors={registrationErrors}
                    name='dob'
                    errorMessage={registrationErrors.includes('dob') && registrationErrors.length === 1 ? error : ''}
                />
                <Input type='tel' errorMessage={registrationErrors.includes('mobile') && registrationErrors.length === 1 ? error : ''} errors={registrationErrors} name='mobile' value={mobile} setValue={setMobile} label='Mobile Number (required)' />
                {/* <label className='fieldHeading'>Address</label> */}
                <AddressSearch
                    errorMessage={registrationErrors.includes('address') && registrationErrors.length === 1 ? error : ''}
                    errors={registrationErrors}
                    value={address}
                    setValue={setAddress}
                    setLockAddressManualFields={setLockAddressManualFields}
                    setAddressManualFill={setAddressManualFill}
                    addressManualFill={addressManualFill}
                    setStreetNumber={setStreetNumber}
                    setStreetName={setStreetName}
                    setSuburb={setSuburb}
                    setState={setState}
                    setPostcode={setPostcode}
                    onLoadingChange={setAddressLookupLoading}
                />
                {addressManualFill && (
                    <>
                        <Input disabled={lockAddressManualFields} type='text' errorMessage={registrationErrors.includes('streetNumber') && registrationErrors.length === 1 ? error : ''} errors={registrationErrors} name='streetNumber' value={streetNumber} setValue={setStreetNumber} label='Unit number/Street number/Property number' />
                        <Input disabled={lockAddressManualFields} type='text' errorMessage={registrationErrors.includes('streetName') && registrationErrors.length === 1 ? error : ''} errors={registrationErrors} name='streetName' value={streetName} setValue={setStreetName} label='Street Name' />
                        <div className='flexrow' style={{ gap: '16px' }}>
                            <Input disabled={lockAddressManualFields} type='text' errorMessage={registrationErrors.includes('suburb') && registrationErrors.length === 1 ? error : ''} errors={registrationErrors} name='suburb' value={suburb} setValue={setSuburb} label='Suburb' />
                            <div style={{ flex: 1 }}>
                                <Dropdown disabled={lockAddressManualFields} options={australianStateOptions} value={state} setValue={setState} label='State' errorFields={registrationErrors} name='state' />
                            </div>
                        </div>
                        <Input disabled={lockAddressManualFields} type='text' errorMessage={registrationErrors.includes('postcode') && registrationErrors.length === 1 ? error : ''} errors={registrationErrors} name='postcode' value={postcode} setValue={setPostcode} label='Postcode' />
                        {lockAddressManualFields && <label>Chose wrong address? <span style={{ textDecorationLine: 'underline', cursor: 'pointer' }} onClick={() => { setAddressManualFill(false); setLockAddressManualFields(false); setAddress(''); setStreetNumber(''); setStreetName(''); setSuburb(''); setState(''); setPostcode(''); }}>Enter again.</span></label>}
                    </>
                )}
                {error && <p style={{ color: 'red' }}>{error}</p>}
                <div style={{ display: 'flex', marginTop: '20px' }}>
                    <button style={{ marginLeft: 'auto', display: 'flex' }} disabled={isRegistrationBlocked} onClick={handleRegister}>
                        {submitting ? 'Loading...' : addressLookupLoading ? 'Loading Address...' : 'Next'}
                    </button>
                </div>
                {pilotReturn}
            </div>
        );
    }

    if (postCompletionStage === 'lln' && !TEMP_FORCE_STUDENT_STAGE_KEY) {
        return (
            <div className='form-wrapper qld-branded-shell'>
                <div className='qld-page-brand'>
                    <div>
                        <h1>White Card QLD Language, Literacy, Numeracy and Digital Assessment</h1>
                        <p>LLND has now been submitted.</p>
                    </div>
                    <img src={Logo} alt='Upskill Institute' />
                </div>
                <p>Click Next to start your EOI.</p>
                {pilotReturn}
                <div style={{ display: 'flex', marginTop: '20px' }}>
                    <button style={{ marginLeft: 'auto', display: 'flex' }} onClick={() => setPostCompletionStage('')}>
                        Next
                    </button>
                </div>
            </div>
        );
    }

    if (postCompletionStage === 'eoi' && !TEMP_FORCE_STUDENT_STAGE_KEY) {
        return (
            <div className='form-wrapper qld-branded-shell'>
                <div className='qld-page-brand'>
                    <div>
                        <h1>Evidence of Identity - QLD White Card Course</h1>
                        <p>Evidence of Identity has been submitted.</p>
                    </div>
                    <img src={Logo} alt='Upskill Institute' />
                </div>
                <p>Please wait for your Trainer to review.</p>
                {pilotReturn}
            </div>
        );
    }

    if (allStagesComplete && !TEMP_FORCE_STUDENT_STAGE_KEY) {
        return (
            <div className='form-wrapper qld-branded-shell'>
                <div className='qld-page-brand'>
                    <div>
                        <h1>Student Declaration Submitted.</h1>
                        <p>Please wait for your Trainers next instruction.</p>
                    </div>
                    <img src={Logo} alt='Upskill Institute' />
                </div>
                {pilotReturn}
            </div>
        );
    }

    if (!activeStageKey && !TEMP_FORCE_STUDENT_STAGE_KEY) {
        const blockedStage = studentDoc?.stages
            ? QLD_STAGE_KEYS.find((key) => ['not_satisfactory', 'cannot_verify'].includes(studentDoc.stages[key]?.reviewStatus))
            : null;

        return (
            <div className='form-wrapper'>
                <h1>Thanks for submitting</h1>
                <p>
                    {blockedStage
                        ? `Your ${QLD_STAGE_LABELS[blockedStage]} could not be approved. Please wait for your trainer's next instruction.`
                        : pendingTrainerReviewStage
                            ? 'Your trainer is reviewing your latest submission.'
                            : 'Please wait for the next section to be loaded by your trainer.'}
                </p>
                {pendingTrainerReviewStage && <p>Current review: {QLD_STAGE_LABELS[pendingTrainerReviewStage]}</p>}
                {nextLockedStage && <p>Next stage: {QLD_STAGE_LABELS[nextLockedStage]}</p>}
                {pilotReturn}
            </div>
        );
    }

    return (
        <div className='form-wrapper qld-branded-shell'>
            <QldStageRenderer
                stageKey={activeStageKey}
                studentDoc={studentDoc}
                onStageProgress={(responseData) => persistStageProgress(activeStageKey, responseData, false)}
                onStageComplete={(responseData) => persistStageProgress(activeStageKey, responseData, true)}
            />
            {pilotReturn}
        </div>
    );
};

export default QldEoi;
